import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import { FormProvider, useForm, Controller } from "react-hook-form";
import TextField from "@material-ui/core/TextField";
import DateFnsUtils from '@date-io/date-fns'; // choose your lib
import { KeyboardDatePicker, MuiPickersUtilsProvider, } from '@material-ui/pickers';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { useRecoilState, useSetRecoilState } from 'recoil';

import * as RemindersApi from '../../../../Common/Apis/RemindersApi';
import { IReminder } from '../../store/ReminderStore';
import { IReminderType } from '../../store/ReminderTypeStore';
import { useSpinner } from "../../../../../core/contexts/SpinnerContext";
import { useNotification } from "../../../../../core/contexts/NotificationContext";
import { reminderTabListState, reminderTabIndexState, reminderListRefreshState } from "../../store/ReminderStore";
import AssetList, { IAssetInfo } from "./Components/AssetList";
import { ReminderTypeList } from "./Components/ReminderTypeList";

import moment from 'moment';

interface IProps {
  reminderRow: IReminder | undefined;
}

const ReminderDetail = ({ reminderRow }: IProps) => {

  const [item, setItem] = useState(reminderRow);

  const [isRecurring, setIsRecurring] = useState(false);
  const [isTimeTrigger, setIsTimeTrigger] = useState(false);
  const [timeTriggerThreshold, setTimeTriggerThreshold] = useState("");
  const [timeTriggerInterval, setTimeTriggerInterval] = useState("");
  const [isOdometerTrigger, setIsOdometerTrigger] = useState(false);
  const [odometerTriggerThreshold, setOdometerTriggerThreshold] = useState("");
  const [odometerTriggerInterval, setOdometerTriggerInterval] = useState("");
  const [odometerTriggerTimeBasedDueSoonThresholdInterval, setOdometerTriggerTimeBasedDueSoonThresholdInterval] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [assetMake, setAssetMake] = useState("");
  const [assetModel, setAssetModel] = useState("");
  const [assetYear, setAssetYear] = useState("");
  const [assetVin, setAssetVin] = useState("");
  const [assetOdometer, setAssetOdometer] = useState("");


  const [reminderTabList, setReminderTabList] = useRecoilState(reminderTabListState);
  const [listRefresh, setListRefresh] = useRecoilState(reminderListRefreshState);
  const setTabIndex = useSetRecoilState(reminderTabIndexState);

  const { setSpinner } = useSpinner();
  const { showSuccess } = useNotification();

  const today = moment().add(+1, 'd');


  const reactHookFormMethod = useForm({
    defaultValues: {
      reminderType: item ? { label: item.ReminderTypeName, value: item.ReminderTypeId } : { label: "", value: "" },
      reminderTypeId: item?.ReminderTypeId ?? "",
      asset: item ? { label: item.AssetPlate + "-" + item.AssetCode, value: item.AssetId } : { label: "", value: "" },
      assetId: item?.AssetId ?? "",
      timeTriggerNextDue: item?.TimeTriggerNextDueDateUtc ? moment(item.TimeTriggerNextDueDateUtc).format("YYYY-MM-DD") : null,
      odometerTriggerNextDue: item?.OdometerTriggerNextDueOdometerKm ?? "",
      operationalHoursTriggerNextDueOperationalHours: "0",
      notes: item?.Notes ?? "",
    }
  });

  const { control, handleSubmit, setValue, watch, reset, formState: { errors } } = reactHookFormMethod;

  const watchReminderType = watch("reminderTypeId");

  useEffect(() => {
    if (reminderRow) {

      //activate this tab
      const thisIndex = reminderTabList
        .findIndex((i) => i.ReminderId === reminderRow.ReminderId);
      setTabIndex(thisIndex + 2);

      setItem(reminderRow);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (item) {
      setAssetMake(item.AssetMake);
      setAssetModel(item.AssetModel);
      setAssetYear(item.AssetYearOfManufacture);
      setAssetVin(item.AssetVin);

      reset({
        reminderType: item ? { label: item.ReminderTypeName, value: item.ReminderTypeId } : { label: "", value: "" },
        reminderTypeId: item?.ReminderTypeId ?? "",
        asset: item ? { label: item.AssetPlate + "-" + item.AssetCode, value: item.AssetId } : { label: "", value: "" },
        assetId: item?.AssetId ?? "",
        timeTriggerNextDue: item?.TimeTriggerNextDueDateUtc ? moment(item.TimeTriggerNextDueDateUtc).format("YYYY-MM-DD") : null,
        odometerTriggerNextDue: item?.OdometerTriggerNextDueOdometerKm ?? "",
        operationalHoursTriggerNextDueOperationalHours: "0",
        notes: item?.Notes ?? "",
      });
    }

    setAssetOdometer("");
  }, [item]);

  const setStateInitial = () => {
    setIsRecurring(false);
    setIsTimeTrigger(false);
    setTimeTriggerThreshold("");
    setTimeTriggerInterval("");
    setIsOdometerTrigger(false);
    setOdometerTriggerInterval("");
    setOdometerTriggerThreshold("");
    setOdometerTriggerTimeBasedDueSoonThresholdInterval("");
    setCategory("");
    setSubcategory("");
    setAssetMake("");
    setAssetModel("");
    setAssetYear("");
    setAssetVin("");
    setAssetOdometer("");

    setValue("timeTriggerNextDue", null);
    setValue("odometerTriggerNextDue", "");
  };

  useEffect(() => {

    const setReminderType = async (reminderType: string) => {
      //console.log(reminderType);

      if (reminderType === "") {
        setStateInitial();
        return;
      }

      try {

        const resData: IReminderType = await RemindersApi.getReminderType(reminderType);
        setIsRecurring(resData.IsRecurring);
        setIsTimeTrigger(resData.TimeTriggerRequired);
        setTimeTriggerThreshold(resData.TimeTriggerDueSoonThresholdIntervalValue ? resData.TimeTriggerDueSoonThresholdIntervalValue + " " + resData.TimeTriggerDueSoonThresholdIntervalTypeName : "");
        setTimeTriggerInterval(resData.TimeTriggerRepeatIntervalValue ? resData.TimeTriggerRepeatIntervalValue + " " + resData.TimeTriggerRepeatIntervalTypeName : "");

        setIsOdometerTrigger(resData.OdometerTriggerRequired);
        setOdometerTriggerThreshold(resData.OdometerTriggerDueSoonThresholdIntervalKm ? resData.OdometerTriggerDueSoonThresholdIntervalKm + "" : "");
        setOdometerTriggerInterval(resData.OdometerTriggerRepeatIntervalKm ? resData.OdometerTriggerRepeatIntervalKm + "" : "");
        setOdometerTriggerTimeBasedDueSoonThresholdInterval(resData.OdometerTriggerTimeBasedDueSoonThresholdIntervalValue
          ? resData.OdometerTriggerTimeBasedDueSoonThresholdIntervalValue + " " + resData.OdometerTriggerTimeBasedDueSoonThresholdIntervalTypeName : "");

        setCategory(resData.CategoryName);
        setSubcategory(resData.SubCategoryName);

        if (!resData.TimeTriggerRequired) setValue("timeTriggerNextDue", null);
        if (!resData.OdometerTriggerRequired) setValue("odometerTriggerNextDue", "");

      } catch (err) {
        console.log('err', err);
      }

    };


    setReminderType(watchReminderType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchReminderType]);



  const resetForm = () => {
    setStateInitial();
    reset();
  };

  const assetInfoBind = (assetInfo: IAssetInfo) => {
    setAssetMake(assetInfo.make);
    setAssetModel(assetInfo.model);
    setAssetYear(assetInfo.year);
    setAssetVin(assetInfo.vin);
    setAssetOdometer(assetInfo.odometer);
  };

  const completeReminder = async () => {

    if (!window.confirm("Do you want to complete this reminder?")) {
      return;
    }

    setSpinner(true);

    try {
      if (item) {
        const resData: IReminder = await RemindersApi.completeReminder(item.ReminderId);

        showSuccess("success", "sucessfully saved", "", 3000, null);
        setItem(resData);
        setListRefresh(!listRefresh);
      }
    }
    catch (err) {
      console.log("onSubmit.err", err);
    }


    setSpinner(false);
  };

  const setCurrentStatus = (statusName: string) => {
    let statusClass = "contents-normal";
    if (statusName.toLowerCase() === "overdue") {
      statusClass = "contents-error";
    }
    else if (statusName.toLowerCase() === "duesoon") {
      statusClass = "contents-warning";
    }

    return (
      <span className={`contents-bolder ${statusClass}`}       >
        {statusName}
      </span>
    );
  };

  const setCompliance = (compliance: boolean | null) => {

    let statusClass = "contents-normal";
    let contents = "On-time";

    if (compliance) {
      statusClass = "contents-error";
      contents = "Late";
    }

    return (
      <span className={`contents-bolder ${statusClass}`}       >
        {contents}
      </span>
    );

  };

  const compareDateTime = (srcDateTime: string, targetDateTime: string) => {

    //console.log(srcDateTime, targetDateTime);

    const srcDate = srcDateTime.length > 10 ? srcDateTime.substring(0, 10) : "";
    const targetDate = targetDateTime.length > 10 ? targetDateTime.substring(0, 10) : "";
    //console.log(srcDate, targetDate);
    if (srcDate === targetDate) {
      return true;
    }

    return false;

  };

  const onSubmit = handleSubmit(async (data) => {

    setSpinner(true);

    try {

      const reminderDto: RemindersApi.IReminderInputDto = {
        AssetId: data.assetId,
        ReminderTypeId: data.reminderTypeId,
        OdometerTriggerNextDueOdometerKm: data.odometerTriggerNextDue !== "" ? data.odometerTriggerNextDue : null,
        TimeTriggerNextDueDateUtc: data.timeTriggerNextDue ? moment(data.timeTriggerNextDue).format("YYYY-MM-DD") + "T02:00:00+13:00" : null,
        OperationalHoursTriggerNextDueOperationalHours: null,
        Notes: item ? data.notes : null
      };

      if (item) {

        if (item.ReminderTypeId === reminderDto.ReminderTypeId
          && compareDateTime(item.TimeTriggerNextDueDateUtc ?? "", reminderDto.TimeTriggerNextDueDateUtc ?? "")
          && (item.OdometerTriggerNextDueOdometerKm ?? "") === (reminderDto.OdometerTriggerNextDueOdometerKm ?? "")
        ) {

          if (data.notes.trim() !== "") {
            //Update Note only
            await RemindersApi.updateReminderNote(item.ReminderId, data.notes);
          }
          else {
            //Delete Note only
            await RemindersApi.deleteReminderNote(item.ReminderId);
          }

        }
        else {
          //Update Reminder
          await RemindersApi.updateReminder(item.ReminderId, reminderDto);
        }

        const filtered = reminderTabList.filter((i) => {
          return i.ReminderTypeId !== item.ReminderTypeId;
        });

        setReminderTabList(filtered);

      }
      else {
        //insert
        await RemindersApi.addReminder(reminderDto);
        resetForm();
      }

      showSuccess("success", "sucessfully saved", "", 3000, null);

      setTabIndex(0);
      setListRefresh(!listRefresh);
    }
    catch (err) {
      console.log("onSubmit.err", err);
    }


    setSpinner(false);

  });
  return (
    <>
      <FormProvider {...reactHookFormMethod} >
        <form >
          <div className="row">
            {/* Left panel - Start */}
            <div className="col-lg-6  mt-3">
              <div className="tab-content" id="myTabContent">
                <div className="card">
                  <div className="card-body">
                    <div className="row mt-3">
                      <div className="col-6 ">
                        <Button
                          type="button"
                          className="mb-3 ml-3"
                          style={{ fontWeight: "600" }}
                          variant="contained"
                          color="primary"
                          onClick={onSubmit}
                        >
                          Save
                        </Button>
                        {item &&
                          <Button
                            type="button"
                            className="mb-3 ml-3"
                            style={{ fontWeight: "600" }}
                            variant="contained"
                            color="primary"
                            onClick={completeReminder}
                          >
                            Complete
                          </Button>
                        }
                      </div>
                      {!item &&
                        <div className="col-6 text-right ">
                          <Button
                            type="button"
                            className="mb-3 ml-3"
                            style={{ fontWeight: "600" }}
                            variant="contained"
                            color="default"
                            onClick={resetForm}
                          >
                            Reset
                          </Button>
                        </div>
                      }
                    </div>
                    <div className="row mt-3">
                      <div className="col-md-6">
                        <div className="p-number">
                          <ReminderTypeList />
                        </div>
                        <span className="p-num-span mr-2">
                          Reminder Type
                        </span>
                        {errors.reminderType && <p className="text-danger2">should not be blank</p>}
                      </div>
                    </div>
                    <div className="row mt-3">
                      <div className="col-md-6">
                        <div className="p-number">
                          <AssetList
                            readOnly={item ? true : false}
                            assetInfoBind={assetInfoBind}
                          />
                        </div>
                        <span className="p-num-span mr-2">
                          Asset
                        </span>
                        {errors.asset && <p className="text-danger2">should not be blank</p>}
                      </div>
                      <div className="col-md-6" style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <div style={{ width: '100%' }}>
                          <div className=" quote-result min-height-22">
                            <span
                              className=""
                            >
                              {assetVin}
                            </span>
                          </div>
                          <span className="p-num-span mr-2">
                            Vin
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="row mt-3">
                      <div className="col-md-4">
                        <div className="quote-result min-height-22">
                          <span
                            className=""
                          >
                            {assetMake}
                          </span>
                        </div>
                        <span className="p-num-span mr-2">
                          Make
                        </span>
                      </div>
                      <div className="col-md-4">
                        <div className="quote-result min-height-22">
                          <span
                            className=""
                          >
                            {assetModel}
                          </span>
                        </div>
                        <span className="p-num-span mr-2">
                          Model
                        </span>
                      </div>
                      <div className="col-md-4">
                        <div className="quote-result min-height-22">
                          <span
                            className=""
                          >
                            {assetYear}
                          </span>
                        </div>
                        <span className="p-num-span mr-2">
                          Year
                        </span>
                      </div>


                    </div>

                    <div className="row mt-3">
                      <div className="col-md-6">
                        <div className="quote-result min-height-22">
                          <span
                            className=""
                          >
                            {category}
                          </span>
                        </div>
                        <span className="p-num-span mr-2">
                          Category
                        </span>
                      </div>

                      <div className="col-md-6">
                        <div className=" quote-result min-height-22">
                          <span
                            className=""
                          >
                            {subcategory}
                          </span>
                        </div>
                        <span className="p-num-span mr-2">
                          Subcategory
                        </span>
                      </div>
                    </div>
                    {item &&
                      <div className="row mt-3">
                        <div className="col-md-6">
                          <div className=" quote-result min-height-22">
                            <span
                              className=""
                            >
                              {setCurrentStatus(item?.StatusName ?? "")}
                            </span>
                          </div>
                          <span className="p-num-span mr-2">
                            Status
                          </span>
                        </div>
                        <div className="col-md-6">
                          <div className=" quote-result min-height-22">
                            <span
                              className=""
                            >
                              {item?.CurrentOdometerKm ?? ""}
                            </span>
                          </div>
                          <span className="p-num-span mr-2">
                            Current Odometer (km)
                          </span>
                        </div>
                      </div>
                    }
                    <div className="row mt-3">
                      <div className="col-6">
                        <div className="p-number">

                          <Controller
                            name="timeTriggerNextDue"
                            rules={{ required: isTimeTrigger }}
                            control={control}
                            render={({ field }) => (

                              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                <KeyboardDatePicker
                                  {...field}
                                  ref={null}
                                  variant='inline'
                                  inputVariant='outlined'
                                  size="small"
                                  disableToolbar
                                  autoOk={true}
                                  format="dd/MM/yyyy"
                                  error={errors.timeTriggerNextDue ? true : false}
                                  helperText={""}
                                  minDate={today}
                                  disabled={!isTimeTrigger}
                                />
                              </MuiPickersUtilsProvider>

                            )}
                          />
                        </div>
                        <span className="p-num-span mr-2">Next Due Date</span>
                      </div>
                      <div className="col-6">
                        <div className="p-number">
                          <Controller
                            name="odometerTriggerNextDue"
                            rules={{ required: isOdometerTrigger, min: 1 }}
                            control={control}
                            render={({ field }) => <TextField
                              {...field}
                              type="number"
                              style={{ width: "100%" }}
                              label=""
                              variant="outlined"
                              size="small"
                              error={errors.odometerTriggerNextDue ? true : false}
                              helperText={""}
                              disabled={!isOdometerTrigger}
                            />}
                          />
                        </div>
                        <span className="p-num-span mr-2">Next Due Odometer(km)</span>
                        <div className="p-num-span mr-2 opt-l-number">
                          {!item && assetOdometer !== "" && `* Current Odometer : ${assetOdometer}`}
                        </div>
                      </div>
                    </div>

                    {item &&
                      <div className="row mt-3">
                        <div className="col-md-4">
                          <div className=" quote-result min-height-22">
                            <span
                              className=""
                            >
                              {item?.TimeTriggerLastCompletedDateUtc ? moment(item.TimeTriggerLastCompletedDateUtc).format("DD/MM/YYYY HH:MM:SS") : ""}
                            </span>
                          </div>
                          <span className="p-num-span mr-2">
                            Last Completed Date
                          </span>
                        </div>
                        <div className="col-md-4">
                          <div className=" quote-result min-height-22">
                            <span
                              className=""
                            >
                              {item?.OdometerTriggerLastCompletedOdometerKm ?? ""}
                            </span>
                          </div>
                          <span className="p-num-span mr-2">
                            Last Completed Odometer (km)
                          </span>
                        </div>
                        <div className="col-md-4">
                          <div className=" quote-result min-height-22">
                            <span
                              className=""
                            >
                              {setCompliance(item?.OverDueAtLastCompletion)}
                            </span>
                          </div>
                          <span className="p-num-span mr-2">
                            Compliance
                          </span>
                        </div>
                      </div>
                    }
                    {item &&
                      <div className="row mt-3">
                        <div className="col">
                          <div className="p-number">
                            <Controller
                              name="notes"
                              control={control}
                              render={({ field }) => <TextField
                                {...field}
                                multiline
                                rows={4}
                                style={{ width: "100%" }}
                                label=""
                                variant="outlined"
                                size="small"
                                error={errors.notes ? true : false}
                                helperText={""}
                              />}
                            />
                          </div>
                          <span className="p-num-span mr-2">
                            Notes
                          </span>
                        </div>
                      </div>
                    }
                  </div>
                </div>
              </div>
            </div>
            {/* Left panel - End */}
            {/* Right panel - Start */}
            <div className="col-lg-6  mt-3">
              <div className="tab-content" id="myTabContent">
                <div className="card">
                  <div className="card-body">
                    <div id="map" className="map-full" >

                      <div className="row mt-3">
                        <div className="col-sm-6">
                          <FormControlLabel control={<Checkbox name="isRecurring" color="primary" checked={isRecurring ?? false} disabled />} label="Recurring" />
                        </div>
                      </div>
                      <div className="row mt-3">
                        <div className="col">
                          <FormControlLabel
                            control={<Checkbox name="timeTrigger" color="primary" checked={isTimeTrigger ?? false} disabled />} label="Time Trigger" />
                        </div>
                      </div>
                      <div className="row ">
                        <div className="col-md-6">
                          <div className="quote-result min-height-22">
                            <span
                              className=""
                            >
                              {timeTriggerThreshold}
                            </span>
                          </div>
                          <span className="p-num-span mr-2">
                            Due Soon Threshold
                          </span>
                        </div>

                        <div className="col-md-6">
                          <div className=" quote-result min-height-22">
                            <span
                              className=""
                            >
                              {timeTriggerInterval}
                            </span>
                          </div>
                          <span className="p-num-span mr-2">
                            Repeat Interval
                          </span>
                        </div>
                      </div>


                      <div className="row mt-3">
                        <div className="col">
                          <FormControlLabel control={<Checkbox name="odometerTrigger" color="primary" checked={isOdometerTrigger ?? false} disabled />} label="Odometer Trigger" />
                        </div>
                      </div>
                      <div className="row ">
                        <div className="col-md-6">
                          <div className="quote-result min-height-22">
                            <span
                              className=""
                            >
                              {odometerTriggerThreshold}
                            </span>
                          </div>
                          <span className="p-num-span mr-2">
                            Due Soon Threshold (km)
                          </span>
                        </div>

                        <div className="col-md-6">
                          <div className=" quote-result min-height-22">
                            <span
                              className=""
                            >
                              {odometerTriggerInterval}
                            </span>
                          </div>
                          <span className="p-num-span mr-2">
                            Repeat Interval (Km)
                          </span>
                        </div>
                      </div>

                      <div className="row mt-3">
                        <div className="col-md-6">
                          <div className="quote-result min-height-22">
                            <span
                              className=""
                            >
                              {odometerTriggerTimeBasedDueSoonThresholdInterval}
                            </span>
                          </div>
                          <span className="p-num-span mr-2">
                            Due Soon Threshold (Time)
                          </span>
                        </div>


                      </div>



                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Right panel - End */}
            {/* <div className="col-12  mt-3">
              <div className="tab-content" id="myTabContent">
                <div className="card">
                  <div className="card-body">
                    {item && <ReminderHistory reminderId={item?.ReminderId} />}
                  </div>
                </div>
              </div>
            </div> */}
          </div>
        </form >
      </FormProvider>
    </>
  );
};

export default ReminderDetail;